import React from "react";
import { Helmet } from "react-helmet-async";
import SectionHeading from "../components/SectionHeading";
import SocialLinks from "../components/SocialLinks";
import "./About.css";

/**
 * About Page
 * Company information and mission - premium edition
 */
function About() {
  const values = [
    {
      number: "01",
      title: "Teach First",
      description:
        "We don't just deliver software — we teach you how to use it, integrate it, and own it. Knowledge that stays with your team forever.",
    },
    {
      number: "02",
      title: "No Subscriptions",
      description:
        "One-time purchase. You own it forever. No renewals, no price increases, no vendor lock-in. Just tools that work.",
    },
    {
      number: "03",
      title: "Real Systems",
      description:
        "Every solution is tested on actual systems before it reaches you. Built by practitioners, for practitioners.",
    },
    {
      number: "04",
      title: "Direct Support",
      description:
        "You talk to the people who built your tools — not a ticket queue. Direct access, real answers.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>About — Built by Practitioners | AxiomLabs Solutions</title>
        <meta
          name="description"
          content="AxiomLabs Solutions builds focused software tools for security, compliance, and automation. One problem, one solution, one price. Built by engineers, for engineers."
        />
      </Helmet>
      <div className="about-page">
        {/* Hero */}
        <div className="about-hero">
          <div className="container">
            <SectionHeading
              eyebrow="About Us"
              title="Built by Practitioners"
              subheading="We build focused software tools and teach integration methods. One problem, one solution, one price."
            />
          </div>
        </div>

        <div className="container">
          {/* Mission */}
          <div className="about-section about-mission">
            <div className="about-section__content">
              <span className="about-section__number">Our Mission</span>
              <p className="about-section__text">
                AxiomLabs Solutions builds focused software tools — one problem,
                one solution, one price. We don't just build apps; we create
                workflows that solve real business problems. Beyond building, we
                teach. We show you how to integrate systems, automate processes,
                and truly own your technology stack.
              </p>
              <p className="about-section__text">
                No hand-holding, no vendor lock-in — just knowledge that stays
                with your team. If you found us, you're looking for something
                specific. Not a platform to learn, not a dashboard to configure
                for weeks. You need a workflow that works, an app that solves a
                problem, or knowledge to connect your systems. That's exactly
                what we provide.
              </p>
            </div>
          </div>

          {/* Values */}
          <div className="about-values">
            <h2 className="about-values__title">How We Work</h2>
            <div className="about-values__grid">
              {values.map((value, index) => (
                <div key={index} className="about-value-card">
                  <span className="about-value-card__number">
                    {value.number}
                  </span>
                  <h3 className="about-value-card__title">{value.title}</h3>
                  <p className="about-value-card__description">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Development Process */}
          <div className="about-values about-process">
            <h2 className="about-values__title">Our Development Process</h2>
            <div className="about-values__grid">
              <div className="about-value-card">
                <span className="about-value-card__number">01</span>
                <h3 className="about-value-card__title">Research First</h3>
                <p className="about-value-card__description">
                  We find the problem before we write code. nis2scan came from
                  needing to check our own NIS2 compliance with no good tools
                  available.
                </p>
              </div>
              <div className="about-value-card">
                <span className="about-value-card__number">02</span>
                <h3 className="about-value-card__title">Build and Test</h3>
                <p className="about-value-card__description">
                  Every tool is tested on real systems — real servers, real
                  PDFs, real findings — before it is sold to anyone.
                </p>
              </div>
              <div className="about-value-card">
                <span className="about-value-card__number">03</span>
                <h3 className="about-value-card__title">Ship and Support</h3>
                <p className="about-value-card__description">
                  You buy once. We support it. No subscription, no account
                  required, no renewal emails. If something breaks, email us.
                </p>
              </div>
            </div>
          </div>

          {/* Philosophy */}
          <div className="about-section about-philosophy">
            <div className="about-section__content">
              <span className="about-section__number">Our Philosophy</span>
              <p className="about-section__text">
                We build tools that do one job well, teach you how to use them,
                and then get out of your way. Every workflow we build is tested
                in real environments before release. All solutions run on your
                infrastructure — no cloud dependencies, no data leaving your
                network.
              </p>
              <p className="about-section__text">
                Just focused workflows and apps that do one thing well — no
                complicated platforms, no subscriptions to manage, no features
                you'll never use.
              </p>
            </div>
          </div>

          {/* Connect */}
          <div className="about-connect">
            <h2 className="about-connect__title">Connect With Us</h2>
            <p className="about-connect__text">
              Have questions about our workflows or want to learn integration
              techniques? Reach out through any of the channels below.
            </p>
            <SocialLinks include={['github', 'twitter', 'email']} />
          </div>
        </div>
      </div>
    </>
  );
}

export default About;
